import { useCallback, useRef, useState } from 'react'

import { LOCALE_COOKIE, TAbsoluteLocale, TCountry } from '@src/config/i18n.config'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import type { TRegionDetail } from '@src/server-side/api/fetch-region-details'
import { getCookieStorage } from '@src/utils/enhancedCookie'
import { noop } from '@src/utils/functions/utils'
import { setLocalisationDataInStorage } from '@src/utils/general.utils'
import { getStandardLocale, updateCommunicationPreference } from '@src/utils/i18n.utils'

interface TUseLocaleContextHandlersInput {
  regionDetails: TRegionDetail
  setLocale: (locale: TAbsoluteLocale) => void
  setIsLocaleConfirmationModalOpen: (isLocaleConfirmationModalOpen: boolean) => void
  setConfirmationLocale: (confirmationLocale: TAbsoluteLocale | TCountry | null) => void
}

interface TUseLocaleContextHandlers {
  handleSetLocale: (
    locale: TAbsoluteLocale,
    clientSide?: boolean,
    shouldUpdateCommunicationPreference?: boolean,
  ) => Promise<void> | void
  closeConfirmationModal: (confirm: boolean) => void
  showConfirmationModal: (confirmationLocale: TAbsoluteLocale, callback: () => void) => void
  onConfirm: () => void
}

const useLocaleContextHandlers = ({
  regionDetails,
  setLocale,
  setIsLocaleConfirmationModalOpen,
  setConfirmationLocale,
}: TUseLocaleContextHandlersInput): TUseLocaleContextHandlers => {
  const [onConfirm, setOnConfirm] = useState<() => void>(noop)
  const communicationPreferenceAbortController = useRef<AbortController | null>(null)
  const handleSetLocale = useCallback(
    async (locale: TAbsoluteLocale, clientSide = false, shouldUpdateCommunicationPreference = true) => {
      const loggedIn = AuthUtil.isLoggedIn()
      const updateLocaleSettings = () => {
        setLocalisationDataInStorage(locale, regionDetails)
        getCookieStorage().setCookieLocalized(LOCALE_COOKIE, locale, { isGlobal: true })
        setLocale(locale)
      }
      if (shouldUpdateCommunicationPreference && loggedIn) {
        communicationPreferenceAbortController.current?.abort()
        communicationPreferenceAbortController.current = new AbortController()

        try {
          // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
          const response = await updateCommunicationPreference({
            locale: getStandardLocale(locale),
            regionDetails,
            abortController: communicationPreferenceAbortController.current,
          })
          if (response?.status === 200) {
            updateLocaleSettings()
          }
        } catch (error) {
          if (communicationPreferenceAbortController.current.signal.aborted) return
          console.error('Failed to update preferred language and communication preference: ', error)
        }
      } else {
        updateLocaleSettings()
      }
      if (!clientSide) window.location.reload()
    },
    [regionDetails, setLocale],
  )

  const closeConfirmationModal = useCallback(
    (confirm: boolean) => {
      if (confirm) onConfirm()
      setIsLocaleConfirmationModalOpen(false)
      setOnConfirm(noop)
      setConfirmationLocale(null)
    },
    [setIsLocaleConfirmationModalOpen, setOnConfirm, setConfirmationLocale, onConfirm],
  )

  const showConfirmationModal = useCallback(
    (confirmationLocale: TAbsoluteLocale, callback: () => void) => {
      setConfirmationLocale(confirmationLocale)
      setOnConfirm(() => callback)
      setIsLocaleConfirmationModalOpen(true)
    },
    [setIsLocaleConfirmationModalOpen, setConfirmationLocale],
  )

  return {
    handleSetLocale,
    closeConfirmationModal,
    showConfirmationModal,
    onConfirm,
  }
}

export default useLocaleContextHandlers
